/**
 * Shared zod schemas for form validation.
 *
 * These mirror the backend contracts (app/schemas/*.py). The backend remains
 * the authority -- this layer exists so users get immediate feedback, not so
 * validation can be skipped server-side.
 */
import { z } from 'zod';

import { ACCENT_TOO_LIGHT, HEX_COLOR, isReadableAccent, parseVideoUrl } from '@/utils/branding';
import { phoneProblem, toE164 } from '@/utils/phone';

export const emailSchema = z
  .string()
  .trim()
  .min(1, 'Email is required')
  .email('Enter a valid email');

/** Mirrors validate_password_strength in app/schemas/auth.py. */
export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .max(128, 'Password is too long')
  .regex(/[a-z]/, 'Add a lowercase letter')
  .regex(/[A-Z]/, 'Add an uppercase letter')
  .regex(/\d/, 'Add a number');

export const uuidSchema = z.string().uuid('Invalid identifier');

/**
 * Mirrors validate_phone in app/schemas/auth.py: a real number by its
 * country's rules (libphonenumber), with the backend's messages. The parsed
 * value is E.164 ("+919876543210"), or '' for "no phone". A number without
 * "+" is read as Indian. See src/utils/phone.js.
 */
export const phoneSchema = z
  .string()
  .trim()
  .max(32, 'Phone number is too long')
  .superRefine((value, context) => {
    const problem = phoneProblem(value);
    if (problem) context.addIssue({ code: z.ZodIssueCode.custom, message: problem });
  })
  .transform((value) => toE164(value));

/** A URL that must point at one site, e.g. a LinkedIn or GitHub profile. */
function urlOnHost(host, message) {
  return z
    .string()
    .trim()
    .url('Enter a valid URL')
    .refine(
      (value) => {
        try {
          const { hostname } = new URL(value);
          return hostname === host || hostname.endsWith(`.${host}`);
        } catch {
          return false;
        }
      },
      { message },
    )
    .optional()
    .or(z.literal('').transform(() => undefined));
}

const optionalUrl = z
  .string()
  .trim()
  .url('Enter a valid URL')
  .optional()
  .or(z.literal('').transform(() => undefined));

// --------------------------------------------------------------------------- //
// Authentication
// --------------------------------------------------------------------------- //
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
});

export const studentRegisterSchema = z.object({
  full_name: z.string().trim().min(2, 'Enter your full name').max(150),
  email: emailSchema,
  password: passwordSchema,
  university_id: z
    .string()
    .uuid()
    .optional()
    .or(z.literal('').transform(() => undefined)),
  university_email: emailSchema.optional().or(z.literal('').transform(() => undefined)),
  accepted_terms: z.literal(true, {
    errorMap: () => ({ message: 'You must accept the terms' }),
  }),
});

export const companyRegisterSchema = z.object({
  full_name: z.string().trim().min(2, 'Enter your full name').max(150),
  email: emailSchema,
  password: passwordSchema,
  company_name: z.string().trim().min(2, 'Enter the company name').max(200),
  industry_id: z
    .string()
    .uuid()
    .optional()
    .or(z.literal('').transform(() => undefined)),
  website: optionalUrl,
  company_size: z.string().optional(),
  location: z.string().max(150).optional(),
  accepted_terms: z.literal(true, {
    errorMap: () => ({ message: 'You must accept the terms' }),
  }),
});

export const otpSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'Enter the 6-digit code'),
});

export const forgotPasswordSchema = z.object({ email: emailSchema });

export const resetPasswordSchema = z
  .object({
    email: emailSchema,
    code: z
      .string()
      .trim()
      .regex(/^\d{6}$/, 'Enter the 6-digit code'),
    new_password: passwordSchema,
    confirm_password: z.string(),
  })
  .refine((values) => values.new_password === values.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  });

/** The password step of a reset, once the code has been checked. */
export const newPasswordSchema = z
  .object({
    new_password: passwordSchema,
    confirm_password: z.string(),
  })
  .refine((values) => values.new_password === values.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  });

// --------------------------------------------------------------------------- //
// Student profile
// --------------------------------------------------------------------------- //
/**
 * Free text that has to say something: "778888" is not a degree or a place.
 * Mirrors StudentProfileUpdate._words in app/schemas/student.py. Blank is
 * allowed and clears the field.
 */
function words(max) {
  return z
    .string()
    .trim()
    .max(max, `Keep it under ${max} characters`)
    .refine((value) => value === '' || /\p{L}/u.test(value), {
      message: 'Use words, not only numbers or symbols',
    })
    .optional();
}

const VOWELS = /[aeiouy]/;

/**
 * Keyboard mash ("aefwefwfwefw", "dfcgvhbjnmkdfghjn"), judged per Latin word
 * of five letters or more: no vowel, six consonants in a row, or the same two
 * letters three times over. Mirrors looks_like_gibberish in app/utils/text.py.
 */
export function looksLikeGibberish(value) {
  const latinWords =
    String(value ?? '')
      .toLowerCase()
      .match(/[a-z]+/g) ?? [];
  return latinWords.some((word) => {
    if (word.length < 5) return false;
    if (!VOWELS.test(word)) return true;
    if (/[^aeiouy]{6,}/.test(word)) return true;
    const pairs = {};
    for (let index = 0; index < word.length - 1; index += 1) {
      const pair = word.slice(index, index + 2);
      pairs[pair] = (pairs[pair] ?? 0) + 1;
      if (pairs[pair] >= 3) return true;
    }
    return false;
  });
}

/** Free text with a minimum length (when given), real words, no mash. */
function realWords(max, { min = 0, minMessage = '' } = {}) {
  return z
    .string()
    .trim()
    .max(max, `Keep it under ${max} characters`)
    .refine((value) => value === '' || value.length >= min, { message: minMessage })
    .refine((value) => value === '' || /\p{L}/u.test(value), {
      message: 'Use words, not only numbers or symbols',
    })
    .refine((value) => value === '' || !looksLikeGibberish(value), {
      message: "This doesn't look like real words. Check for typos.",
    })
    .optional();
}

/** Mirror the limits in app/schemas/student.py. */
export const HEADLINE_LIMITS = { min: 10, max: 150 };
export const BIO_LIMITS = { min: 50, max: 4000 };
export const EARLIEST_GRADUATION_YEAR = 2000;
export const MIN_AGE_YEARS = 10;
export const MAX_AGE_YEARS = 100;
export const AVAILABLE_FROM_YEARS_AHEAD = 2;
/** The highest monthly stipend a student can ask for. */
export const MAX_STIPEND = 1_000_000;

/** Mirrors PortfolioCreate / PortfolioUpdate in app/schemas/student.py. */
export const portfolioProjectSchema = z.object({
  title: z
    .string()
    .trim()
    .min(2, 'Enter a title of at least 2 characters')
    .max(200, 'Keep it under 200 characters')
    .regex(/\p{L}/u, 'Use words, not only numbers or symbols'),
  description: words(2000),
  project_url: z
    .string()
    .trim()
    .url('Enter a full link starting with https://')
    .optional()
    .or(z.literal('')),
  repository_url: z
    .string()
    .trim()
    .url('Enter a full link starting with https://')
    .optional()
    .or(z.literal('')),
});

export const studentProfileSchema = z.object({
  headline: realWords(HEADLINE_LIMITS.max, {
    min: HEADLINE_LIMITS.min,
    minMessage: `Use at least ${HEADLINE_LIMITS.min} characters for your headline`,
  }),
  // A bio is prose, so it is not checked for mash -- one odd word in a
  // paragraph is not a typo.
  bio: z
    .string()
    .trim()
    .max(BIO_LIMITS.max, `Keep it under ${BIO_LIMITS.max} characters`)
    .refine((value) => value === '' || value.length >= BIO_LIMITS.min, {
      message: `Use at least ${BIO_LIMITS.min} characters for your bio`,
    })
    .refine((value) => value === '' || /\p{L}/u.test(value), {
      message: 'Use words, not only numbers or symbols',
    })
    .optional(),
  location: realWords(150),
  university_id: z
    .string()
    .uuid()
    .optional()
    .or(z.literal('').transform(() => undefined)),
  degree: realWords(150),
  field_of_study: realWords(150),
  graduation_year: z
    .union([
      z.coerce
        .number({ invalid_type_error: 'Choose a year' })
        .int('Choose a year')
        // A recent graduate, or the year they expect to graduate. Mirrors
        // StudentProfileUpdate in app/schemas/student.py.
        .refine((year) => year >= minGraduationYear() && year <= maxGraduationYear(), {
          message: `Choose a year from ${minGraduationYear()} to ${maxGraduationYear()}`,
        }),
      z.literal(''),
    ])
    .optional()
    .transform((value) => (value === '' ? undefined : value)),
  preferred_work_mode: z
    .enum(['ONSITE', 'REMOTE', 'HYBRID'])
    .optional()
    .or(z.literal('').transform(() => undefined)),
  preferred_location: realWords(150),
  // Empty date and number inputs come back as '', which is neither a date nor
  // a number -- send undefined so the field is simply left alone.
  // Old enough for an internship, and not a mistyped year.
  date_of_birth: z
    .union([
      z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the date picker')
        .refine((value) => value <= latestBirthDate(), {
          message: `Choose a date of birth in ${latestBirthYear()} or earlier`,
        })
        .refine((value) => value >= earliestBirthDate(), 'Check the year of your date of birth'),
      z.literal(''),
    ])
    .optional()
    .transform((value) => (value === '' ? undefined : value)),
  // A past date just means "available now"; far ahead is a typo.
  available_from: z
    .union([
      z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the date picker')
        .refine((value) => value <= latestAvailableFrom(), {
          message: `Choose a date within ${AVAILABLE_FROM_YEARS_AHEAD} years from today`,
        }),
      z.literal(''),
    ])
    .optional()
    .transform((value) => (value === '' ? undefined : value)),
  // Blank means "no expectation" and is sent as null, so clearing the box
  // clears the value. (A blank box used to be coerced to 0 and saved as 0.)
  // Mirrors StudentProfileUpdate in app/schemas/student.py.
  expected_stipend_min: z
    .preprocess((value) => (value == null ? '' : String(value).trim()), z.string())
    .refine((value) => value === '' || /^\d+(\.\d{1,2})?$/.test(value), {
      message: 'Enter an amount like 15000 or 15000.50',
    })
    .refine((value) => value === '' || Number(value) > 0, {
      message: 'Enter an amount above 0, or leave it blank',
    })
    .refine((value) => value === '' || Number(value) <= MAX_STIPEND, {
      message: `Enter ${MAX_STIPEND.toLocaleString('en-US')} or less`,
    })
    .transform((value) => (value === '' ? null : Number(value))),
  portfolio_url: optionalUrl,
  linkedin_url: urlOnHost('linkedin.com', 'Enter a linkedin.com link'),
  github_url: urlOnHost('github.com', 'Enter a github.com link'),
  open_to_opportunities: z.boolean().optional(),
});

/** The latest graduation year a student can enter: this year plus six. */
export function maxGraduationYear() {
  return new Date().getFullYear() + 6;
}

/** The earliest graduation year a student can choose. */
export function minGraduationYear() {
  return EARLIEST_GRADUATION_YEAR;
}

/** Every year a student can choose, newest first, for a select. */
export function graduationYears() {
  const years = [];
  for (let year = maxGraduationYear(); year >= minGraduationYear(); year -= 1) years.push(year);
  return years;
}

/** The date `years` from today (negative: in the past) as local YYYY-MM-DD. */
function isoYearsFromToday(years) {
  const now = new Date();
  const shifted = new Date(now.getFullYear() + years, now.getMonth(), now.getDate());
  // 29 February in a non-leap year rolls into March; pull it back to the 28th.
  if (shifted.getMonth() !== now.getMonth()) shifted.setDate(0);
  const month = String(shifted.getMonth() + 1).padStart(2, '0');
  const day = String(shifted.getDate()).padStart(2, '0');
  return `${shifted.getFullYear()}-${month}-${day}`;
}

/** The latest birth year allowed, judged by year rather than to the day. */
export function latestBirthYear() {
  return new Date().getFullYear() - MIN_AGE_YEARS;
}

/** The latest allowed date of birth: 31 December of `latestBirthYear()`. */
export function latestBirthDate() {
  return `${latestBirthYear()}-12-31`;
}

export function earliestBirthDate() {
  return isoYearsFromToday(-MAX_AGE_YEARS);
}

export function latestAvailableFrom() {
  return isoYearsFromToday(AVAILABLE_FROM_YEARS_AHEAD);
}

// --------------------------------------------------------------------------- //
// Account (PATCH /auth/me, POST /auth/change-password, POST /auth/me/delete)
// --------------------------------------------------------------------------- //
export const accountDetailsSchema = z.object({
  full_name: z
    .string()
    .trim()
    .min(2, 'Enter your full name')
    .max(150, 'Name is too long')
    .regex(/\p{L}/u, 'Enter your full name'),
  phone: phoneSchema,
});

/** A new password, typed twice. The current one is not asked for: being
 * signed in is the proof (the server signs out every other session). */
export const changePasswordSchema = z
  .object({
    new_password: passwordSchema,
    confirm_password: z.string().min(1, 'Type the new password again'),
  })
  .refine((values) => values.new_password === values.confirm_password, {
    message: 'Passwords do not match',
    path: ['confirm_password'],
  });

export const closeAccountSchema = z.object({
  password: z.string().min(1, 'Enter your password to confirm'),
});

const dateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the date picker');

export const educationSchema = z
  .object({
    institution_name: z.string().trim().min(2, 'Institution is required').max(200),
    degree: z.string().trim().min(2, 'Degree is required').max(150),
    field_of_study: z.string().trim().max(150).optional(),
    grade: z.string().trim().max(50).optional(),
    start_date: dateString,
    end_date: dateString.optional().or(z.literal('').transform(() => undefined)),
    is_current: z.boolean().default(false),
    description: z.string().trim().max(2000).optional(),
  })
  .refine((values) => !values.end_date || values.end_date >= values.start_date, {
    message: 'End date must be after the start date',
    path: ['end_date'],
  })
  .refine((values) => !(values.is_current && values.end_date), {
    message: 'A current entry cannot have an end date',
    path: ['end_date'],
  })
  // Mirrors EducationCreate/Update: finished (end date) or ongoing (ticked).
  .refine((values) => values.is_current || Boolean(values.end_date), {
    message: 'Add an end date, or tick "I am currently studying here"',
    path: ['end_date'],
  });

export const experienceSchema = z
  .object({
    company_name: z.string().trim().min(2, 'Company is required').max(200),
    title: z.string().trim().min(2, 'Title is required').max(150),
    employment_type: z.string().trim().max(60).optional(),
    location: z.string().trim().max(150).optional(),
    work_mode: z
      .enum(['ONSITE', 'REMOTE', 'HYBRID'])
      .optional()
      .or(z.literal('').transform(() => undefined)),
    start_date: dateString,
    end_date: dateString.optional().or(z.literal('').transform(() => undefined)),
    is_current: z.boolean().default(false),
    description: z.string().trim().max(4000).optional(),
  })
  .refine((values) => !values.end_date || values.end_date >= values.start_date, {
    message: 'End date must be after the start date',
    path: ['end_date'],
  })
  .refine((values) => !(values.is_current && values.end_date), {
    message: 'A current role cannot have an end date',
    path: ['end_date'],
  })
  // Mirrors ExperienceBase: a role has ended (end date) or is ongoing (ticked).
  .refine((values) => values.is_current || Boolean(values.end_date), {
    message: 'Add an end date, or tick "I currently work here"',
    path: ['end_date'],
  });

export const skillSchema = z.object({
  skill_name: z.string().trim().min(1, 'Enter a skill').max(80),
  proficiency: z.enum(['BEGINNER', 'INTERMEDIATE', 'ADVANCED', 'EXPERT']),
});

/** Mirrors CareerGoalRequest in app/schemas/student.py. */
export const careerGoalSchema = z.object({
  goal: z
    .string()
    .trim()
    .min(3, 'Tell us a little more about what you want to do')
    .max(500, 'Keep it under 500 characters'),
});

export const universityEmailSchema = z.object({
  university_email: emailSchema,
});

// --------------------------------------------------------------------------- //
// Company workspace
// --------------------------------------------------------------------------- //
/** Mirrors CompanyProfileUpdate in app/schemas/company.py. */
export const companyProfileSchema = z.object({
  name: z.string().trim().min(2, 'Company name is required').max(200),
  industry_id: uuidSchema.optional().or(z.literal('').transform(() => undefined)),
  company_size: z.string().trim().max(40).optional(),
  description: z.string().trim().max(4000).optional(),
  founded_year: z.coerce
    .number()
    .int()
    .min(1800, 'Enter a valid year')
    .max(new Date().getFullYear(), 'Founding year cannot be in the future')
    .optional()
    .or(z.literal('').transform(() => undefined)),
  location: z.string().trim().max(150).optional(),
  contact_email: emailSchema.optional().or(z.literal('').transform(() => undefined)),
  contact_phone: phoneSchema.optional(),
  website: optionalUrl,
  linkedin_url: optionalUrl,
});

/** Mirrors InternshipCreate/InternshipUpdate in app/schemas/internship.py. */
/** An empty number box means "nothing entered", never 0. */
const blankToUndefined = (value) => (value === '' || value == null ? undefined : value);

export const internshipSchema = z
  .object({
    title: z.string().trim().min(4, 'Title must be at least 4 characters').max(200),
    description: z.string().trim().min(40, 'Describe the role in at least 40 characters').max(8000),
    responsibilities: z.string().trim().max(4000).optional(),
    requirements: z.string().trim().max(4000).optional(),
    perks: z.string().trim().max(2000).optional(),
    job_category_id: uuidSchema.optional().or(z.literal('').transform(() => undefined)),
    industry_id: uuidSchema.optional().or(z.literal('').transform(() => undefined)),
    location: z.string().trim().min(2, 'Add a location').max(150),
    work_mode: z.enum(['ONSITE', 'REMOTE', 'HYBRID']),
    duration_months: z.preprocess(
      blankToUndefined,
      z.coerce
        .number({
          required_error: 'Add the duration in months',
          invalid_type_error: 'Add the duration in months',
        })
        .int('Use whole months')
        .min(1, 'At least 1 month')
        .max(36, 'At most 36 months'),
    ),
    positions: z.preprocess(
      blankToUndefined,
      z.coerce
        .number({
          required_error: 'Add how many positions you are hiring for',
          invalid_type_error: 'Add how many positions you are hiring for',
        })
        .int('Use a whole number')
        .min(1, 'At least 1 position')
        .max(999),
    ),
    is_paid: z.boolean().default(true),
    // Blank is "no amount", not 0: coercing '' gives 0, which would pass as a
    // stipend and hide that none was entered.
    stipend_min: z.preprocess(blankToUndefined, z.coerce.number().min(0).optional()),
    stipend_max: z.preprocess(blankToUndefined, z.coerce.number().min(0).optional()),
    application_deadline: z.string().min(1, 'Add an application deadline'),
    starts_on: z.string().min(1, 'Add a start date'),
  })
  .refine(
    (values) =>
      values.stipend_min === undefined ||
      values.stipend_max === undefined ||
      values.stipend_min <= values.stipend_max,
    { message: 'Minimum must not exceed maximum', path: ['stipend_max'] },
  )
  .superRefine((values, context) => {
    const { application_deadline: deadline, starts_on: startsOn } = values;
    // ISO dates compare as strings. Strictly before: reviewing, interviewing
    // and offering all happen between the two.
    if (!deadline || !startsOn || deadline < startsOn) return;
    // On both fields, naming the other date: either one may be the one to
    // move, and flagging only the deadline sent people to change the wrong one.
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['application_deadline'],
      message: `Applications must close before the internship starts (${readableDate(startsOn)}).`,
    });
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['starts_on'],
      message: `Must be after the application deadline (${readableDate(deadline)}). Move this date later, or the deadline earlier.`,
    });
  })
  .superRefine((values, context) => {
    // A paid internship names its stipend; the backend refuses to publish one
    // that does not.
    if (!values.is_paid) return;
    if (values.stipend_min === undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['stipend_min'],
        message: 'Add the minimum stipend',
      });
    }
    if (values.stipend_max === undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['stipend_max'],
        message: 'Add the maximum stipend',
      });
    }
  })
  .refine((values) => values.is_paid || (!values.stipend_min && !values.stipend_max), {
    message: 'An unpaid internship cannot carry a stipend',
    path: ['stipend_min'],
  });

/** Blank means "clear it": the branding PATCH takes null for that, not "". */
const blankToNull = (value) => (value === '' ? null : value);

export const MAX_BRAND_VALUES = 6;

/**
 * Mirrors CompanyBrandingUpdate in app/schemas/company.py. The editor always
 * sends every field, so the output is the whole PATCH body with blanks as null.
 */
export const companyBrandingSchema = z.object({
  tagline: z
    .string()
    .trim()
    .max(160, 'Keep the tagline under 160 characters')
    .transform(blankToNull),
  accent_color: z
    .string()
    .trim()
    .refine((value) => value === '' || HEX_COLOR.test(value), 'Use a hex colour such as #1d4ed8')
    .refine((value) => value === '' || !HEX_COLOR.test(value) || isReadableAccent(value), {
      message: ACCENT_TOO_LIGHT,
    })
    .transform((value) => (value === '' ? null : value.toLowerCase())),
  mission: z
    .string()
    .trim()
    .max(1000, 'Keep the mission under 1000 characters')
    .transform(blankToNull),
  culture: z.string().trim().max(4000, 'Keep this under 4000 characters').transform(blankToNull),
  values: z
    .array(
      z.object({
        title: z
          .string()
          .trim()
          .min(2, 'A value needs a title of at least 2 characters')
          .max(60, 'Keep the title under 60 characters'),
        description: z
          .string()
          .trim()
          .max(240, 'Keep the description under 240 characters')
          .transform(blankToNull),
      }),
    )
    .max(MAX_BRAND_VALUES, `Up to ${MAX_BRAND_VALUES} values`),
  perks: z.array(z.string()),
  video_url: z
    .string()
    .trim()
    .max(300)
    .refine(
      (value) => value === '' || parseVideoUrl(value) !== null,
      'Paste a YouTube or Vimeo link to a single video',
    )
    .transform(blankToNull),
});

export const galleryCaptionSchema = z
  .string()
  .trim()
  .max(200, 'Keep the caption under 200 characters');

export const companyVerificationSchema = z.object({
  registration_number: z.string().trim().max(100).optional(),
  note: z.string().trim().max(1000).optional(),
});

export const memberInviteSchema = z.object({
  email: emailSchema,
  role: z.enum(['COMPANY_ADMIN', 'HR', 'RECRUITER', 'HIRING_MANAGER']),
  full_name: z.string().trim().max(150).optional(),
  job_title: z.string().trim().max(150).optional(),
});

export const memberRoleSchema = z.object({
  role: z.enum(['COMPANY_ADMIN', 'HR', 'RECRUITER', 'HIRING_MANAGER']),
  job_title: z.string().trim().max(150).optional(),
});

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  page_size: z.coerce.number().int().min(1).max(100).default(20),
});

/** "7 Oct 2026" from "2026-10-07", without timezone shifts. */
function readableDate(isoDate) {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
